import { Controller } from "@hotwired/stimulus"
import Popper from "popper.js"

export default class extends Controller {
  static targets = ["anchor", "content"]
  static values = {
    placement: { type: String, default: "bottom-end" },
    parentContainerActiveClass: { type: String, default: "active" }
  }

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.hide()
  }

  toggle(event) {
    this.popover ? this.hide() : this.open()
  }

  open() {
    if (this.popover) this.hide()

    const content = this.cloneContent()
    if (!content) return

    this.popover = document.createElement("div")
    this.popover.classList.add("popover", "bs-popover-auto", "show", "p-0", "border-0", "shadow-lg", "rounded")
    this.popover.style.minWidth = "280px"
    this.popover.appendChild(content)

    const popperParent = this.parentContainerElement() || document.body
    popperParent.appendChild(this.popover)

    this.popper = new Popper(this.anchorTargetElement(), this.popover, {
      placement: this.placementValue,
      strategy: "absolute",
      modifiers: {
        offset: { offset: "0, 4" },
        preventOverflow: { enabled: false },
      }
    })

    this.pinContainer()
    document.addEventListener("click", this.handleOutsideClick)
    document.addEventListener("keydown", this.handleKeydown)
  }

  hide() {
    if (this.popper) {
      this.popper.destroy()
      this.popper = null
    }

    if (this.popover) {
      this.popover.remove()
      this.popover = null
    }

    this.unpinContainer()
    document.removeEventListener("click", this.handleOutsideClick)
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleOutsideClick(event) {
    if (!this.popover) return
    if (this.popover.contains(event.target) || this.anchorTargetElement()?.contains(event.target)) return
    this.hide()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.hide()
    }
  }

  cloneContent() {
    if (!this.hasContentTarget) return null
    const node = this.contentTarget
    if (node.tagName === "TEMPLATE") {
      return node.content.cloneNode(true)
    }
    return node.cloneNode(true)
  }

  anchorTargetElement() {
    if (this.hasAnchorTarget) return this.anchorTarget
    return this.element
  }

  pinContainer() {
    const container = this.parentContainerElement()
    if (container) container.classList.add(this.parentContainerActiveClassValue)
  }

  unpinContainer() {
    const container = this.parentContainerElement()
    if (container) container.classList.remove(this.parentContainerActiveClassValue)
  }

  // use data-popover-parent-container on the controller element to specify a parent container selector to pin/unpin
  // Note: not using stimulus target API as it selects outside the controller element
  parentContainerElement() {
    if (this.element.dataset.popoverParentContainer) return this.element.closest(this.element.dataset.popoverParentContainer)
    return null
  }
}
